import type { Inbox } from "@mailhand/core/schemas";
import { inboxCreateSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Button, Field, Input } from "../../ui/index.js";

/** POST /inboxes: a new forward address for a support mailbox. */
export function InboxCreateForm({ onCreated }: { onCreated: (inbox: Inbox) => void }) {
  const api = useApi();
  const { view, setView } = useTenant();
  const task = useTask();
  const [displayName, setDisplayName] = useState(`${view.tenant.name} Support`);
  const [replyTo, setReplyTo] = useState("");
  const [errors, setErrors] = useState<{ displayName?: string; replyTo?: string }>({});

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = inboxCreateSchema.safeParse({ displayName, replyTo: replyTo.trim() });
    if (!parsed.success) {
      const paths = new Set(parsed.error.issues.map((issue) => issue.path[0]));
      setErrors({
        ...(paths.has("displayName") ? { displayName: "Type the name senders see, like Acme Support." } : {}),
        ...(paths.has("replyTo") ? { replyTo: "Type your real support address, like support@acmecoffee.com." } : {}),
      });
      return;
    }
    setErrors({});
    await task.run(async () => {
      const inbox = await api.call("inboxCreate", { body: parsed.data });
      setView({ ...view, inboxes: [...view.inboxes, inbox] });
      onCreated(inbox);
    });
  }

  return (
    <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
      <div className="form-grid">
        <Field label="Your support address" hint="Where customers write to you today. Replies say to answer here." error={errors.replyTo}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} type="email" inputMode="email" placeholder="support@yourbusiness.com" value={replyTo} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setReplyTo(event.target.value); }} />
          )}
        </Field>
        <Field label="Name on replies" error={errors.displayName}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} value={displayName} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setDisplayName(event.target.value); }} />
          )}
        </Field>
      </div>
      <div className="button-row">
        <Button type="submit" variant="primary" busy={task.busy}>
          Get my forward address
        </Button>
      </div>
    </form>
  );
}
